import { NgFor, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { BrowserService } from 'app/core/browser/browser.service';
import { CurrentUserService } from 'app/core/user/current-user.service';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { UserComponent } from 'app/layout/common/user/user.component';
import { PageBannerComponent } from 'app/shared/page-banner/page-banner.component';
import { Subject, filter, startWith, takeUntil } from 'rxjs';

/**
 * Display names for the codes in the `groups` claim, which are the backend's spellings of the
 * Contact's working group checkboxes (and double as document tags, hence the terseness). A code
 * missing here is shown as itself rather than dropped, so a group the backend starts sending is
 * visible before anyone names it.
 */
const WORKING_GROUP_NAMES: Record<string, string> = {
    '5G'        : '5G',
    '6G'        : '6G',
    'PATHSS'    : 'PATHSS',
    'PATHSS2024': 'PATHSS 2024',
    'NTIA'      : 'NTIA Multi-Stakeholder Forum',
    'ISAC'      : 'ISAC',
    'COLLAB'    : 'ISAC Collaboration Forum',
};


@Component({
    selector     : 'classy-layout',
    templateUrl  : './classy.component.html',
    styleUrls    : ['./classy.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [FuseLoadingBarComponent, FuseVerticalNavigationComponent, UserComponent, NgIf, NgFor, MatIconModule, MatButtonModule, RouterOutlet, PageBannerComponent],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy
{
    isScreenSmall: boolean;
    navigation: Navigation;

    /**
     * The heading for the page banner, read off the activated route's `data.heading`.
     *
     * Null when the route declares none, and the band is then not rendered at all rather than
     * rendered empty — `tree-harness` is the one route in that state.
     */
    bannerHeading: string | null = null;
    user: User;

    /** The working groups the member belongs to, by display name, from the `groups` claim. */
    workingGroups: string[] = [];
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _currentUser: CurrentUserService,
        private _browser: BrowserService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) =>
            {
                this.user = user;
                this.workingGroups = this._currentUser.groups.map(group => WORKING_GROUP_NAMES[group] ?? group);
            });

        // Track the heading the activated route declares. `startWith` covers the first render,
        // which has already navigated by the time this subscribes.
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                startWith(null),
                takeUntil(this._unsubscribeAll),
            )
            .subscribe(() =>
            {
                this.bannerHeading = this._activeHeading();
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * The `heading` declared by the deepest activated route, or null when it declares none.
     *
     * Walked from the router's own root rather than from this component's `ActivatedRoute`,
     * which is the layout's route and not the one that matched.
     */
    private _activeHeading(): string | null
    {
        let route = this._router.routerState.root;

        while ( route.firstChild )
        {
            route = route.firstChild;
        }

        return route.snapshot.data['heading'] ?? null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    navigateTo(path: string): void {
        if (!path || path.length == 0) return;
        if (path.startsWith("http")) {
            this._browser.open(path);
            return;
        }
            
        this._router.navigate([path]);
    }


    isAdmin(): boolean {
        return this._currentUser.isAdmin();
    }

    canViewSolicitations(): boolean {
        return this._currentUser.canViewSolicitations();
    }

    canSeeWorkingGroups(): boolean {
        return this._currentUser.canSeeWorkingGroups();
    }

    isPrimaryContact(): boolean {
        return this._currentUser.isPrimaryContact();
    }
}
